/* FIT X — GSAP motion engine for the v3-fitx-landing template.
   ==========================================================================

   What this replaces
   ------------------
   The original run (assets/fitx-chapter.js) is one controller that owns every
   chapter at once: chapters overlap by a negative margin, hand over to each
   other with a masked wipe, claim snap stops so a gesture moves exactly one
   chapter, and can refuse to be left until their copy has finished writing.
   It is a single machine, and tuning one chapter means reasoning about the
   whole run.

   This file is the opposite arrangement, and deliberately so: every section
   is independent. A section declares what it wants in data attributes, this
   file builds it one ScrollTrigger and one timeline, and no section can see
   or affect another. Change chapter 04 and nothing else moves.

   What a section declares
   -----------------------
   The scope is any element carrying data-fx. Everything else is read off it:

     data-fx-on           "scroll" (default), "load", or "hero" — the last one
                          being load, held until the hero video has finished
     data-fx-enter        how the section arrives      (see ENTER)
     data-fx-exit         how it leaves                (see EXIT)
     data-fx-from         travel distance in px, for the enters that move
     data-fx-start        ScrollTrigger start, e.g. "top 75%"
     data-fx-exit-start   ScrollTrigger start for the exit
     data-fx-duration     milliseconds
     data-fx-delay        milliseconds before the section's own enter
     data-fx-ease         any GSAP ease string
     data-fx-scrub        "off", or a number of seconds of catch-up
     data-fx-pin          "1" to hold the stage while its scroll is spent
     data-fx-pin-length   how much scroll to hold it for, in % of viewport
     data-fx-once         "1" to play once and never reverse

   And, for what happens INSIDE it:

     data-fx-child        how the non-text pieces arrive   (see ENTER)
     data-fx-child-from   their travel distance in px
     data-fx-lead         milliseconds after the section's enter starts that the
                          pieces begin — negative overlaps them

   And for text, which is animated per character or per word and so is measured
   in terms nothing else on the section is:

     data-fx-text          how text units arrive; "apple" is fade + rise + blur
     data-fx-text-rise     the rise, as a % of the text's OWN size, so a
                           heading and a body line rise proportionally
     data-fx-text-blur     px of blur to resolve out of; 0 for a clean fade
     data-fx-text-duration milliseconds for one unit
     data-fx-beat          milliseconds between one unit and the next inside
                           the same element
     data-fx-gap           milliseconds when the next unit belongs to a
                           different element — reads as a line break

   Any of them may be repeated with a -mb suffix (data-fx-enter-mb) to say
   something different on a phone. Missing means "same as desktop", so a
   section only states the differences.

   Two more, on the elements inside rather than the scope:

     data-fx-item         this is one of the pieces that staggers in
     data-fx-split        "char" or "word" — break the text up first, and the
                          units become the pieces instead of the element

   Nothing here is resolved to a look. This file knows durations, eases and
   distances; what a chapter is made of stays in the section, and what it
   looks like at rest stays in assets/fitx-chapter.css.

   Breakpoints
   -----------
   Everything runs inside gsap.matchMedia(), one context per side of 1024px.
   Crossing the boundary tears down one set of triggers and builds the other,
   so a phone never carries desktop timings and a resize is not a reload.

   Reduced motion
   --------------
   matchMedia carries a third context for prefers-reduced-motion, which sets
   every element to its resting state and builds no triggers at all. The page
   is then exactly what it is with this file blocked, which is the useful
   fallback: all the content, none of the movement.

   Progressive enhancement
   -----------------------
   Nothing is hidden in CSS. Elements start visible and GSAP animates FROM a
   hidden state at init, so with this file blocked — or with GSAP failing to
   load — the page reads as a plain, complete, scrollable document. The cost
   is one flash of the resting state on a slow parse, which prepare() takes
   care of by setting the from-state before the first paint it can reach.
   ========================================================================== */
(function () {
  'use strict';

  /* The theme editor re-runs a section's scripts when it reloads that
     section, so a second run is a signal to rebuild, not a duplicate. */
  if (window.__fitxGsap) {
    window.__fitxGsap.refresh();
    return;
  }

  var DESKTOP = '(min-width: 1024px)';
  var MOBILE = '(max-width: 1023px)';

  /* Defaults the controller section writes, so an unset field on a section
     falls through to one place rather than to a literal in this file. */
  var G = window.FITX_GSAP || {};
  var DEF = {
    enter: G.enter || 'fade-up',
    exit: G.exit || 'none',
    from: num(G.from, 40),
    start: G.start || 'top 78%',
    exitStart: G.exitStart || 'bottom 30%',
    /* Four of these are milliseconds, and they are milliseconds because a
       Shopify range cannot step finer than 0.1: in seconds the smallest legal
       increment is a tenth, which is far too coarse for a stagger. As integer
       ms the same sliders step by 10 and 50. GSAP wants seconds, so ms() does
       the one conversion, at the point of use. */
    duration: num(G.duration, 900),
    delay: num(G.delay, 0),
    ease: G.ease || 'power3.out',
    child: G.child || 'fade-up',
    childFrom: num(G.childFrom, 24),
    lead: num(G.lead, 150),
    text: G.text || 'apple',
    textRise: num(G.textRise, 30),
    textBlur: num(G.textBlur, 5),
    textDuration: num(G.textDuration, 700),
    beat: num(G.beat, 12),
    gap: num(G.gap, 72),
    speed: num(G.speed, 1)
  };

  /* ---- the Apple curve ---------------------------------------------------
     cubic-bezier(0.16, 1, 0.3, 1): almost all of the distance covered in the
     first third, then a long quiet settle. It is what makes text read as
     arriving rather than as moving, and it is the curve the original run used
     for exactly this.

     GSAP cannot parse a cubic-bezier string without CustomEase, which is a
     plugin this theme does not carry, so the curve is solved here. Eight
     Newton-Raphson steps against the x polynomial, which is well inside a
     rounding error for an ease and costs nothing — it runs once per frame per
     tween, on four coefficients.

     expo.out is close and would have done. It is not this, though: it lands a
     touch harder, and the whole point of the effect is the tail. */
  function bezier(x1, y1, x2, y2) {
    function A(a, b) { return 1 - 3 * b + 3 * a; }
    function B(a, b) { return 3 * b - 6 * a; }
    function C(a) { return 3 * a; }
    function calc(t, a, b) { return ((A(a, b) * t + B(a, b)) * t + C(a)) * t; }
    function slope(t, a, b) { return 3 * A(a, b) * t * t + 2 * B(a, b) * t + C(a); }
    return function (x) {
      var t = x;
      for (var i = 0; i < 8; i++) {
        var d = slope(t, x1, x2);
        if (!d) break;
        t -= (calc(t, x1, x2) - x) / d;
      }
      return calc(t, y1, y2);
    };
  }

  function num(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }

  /* Milliseconds in, seconds out, divided by the run's speed on the way
     through — a speed of 2 is twice as fast, which is half as long. */
  function ms(v, fallback, speed) {
    return num(v, fallback) / 1000 / (speed || 1);
  }

  /* ---- the vocabulary ----------------------------------------------------
     Each entry is the state an element comes FROM (for an enter) or goes TO
     (for an exit). `d` is the section's travel distance, so one preset covers
     a 12px nudge and a 160px arrival without becoming two presets.

     autoAlpha rather than opacity throughout: it carries visibility with it,
     so something waiting to arrive is not quietly holding the pointer over
     whatever is under it. */
  var ENTER = {
    none: null,
    /* The text preset, and the default for a section's pieces. Fade, a rise
       measured in the text's OWN size rather than in pixels — so a 64px
       heading and a 16px body line rise proportionally instead of the heading
       barely moving — and a blur, which is the half of it that sells the
       effect: without the blur this is a slide, with it the text resolves.

       Everything it needs is on the config rather than baked in, because the
       blur is the one part with a real cost: it is a filter on every unit, and
       a body split into forty words is forty filtered layers. Dialling it to 0
       leaves a clean fade-and-rise and is the right answer on a slow phone. */
    apple: function (d, c) {
      return {
        autoAlpha: 0,
        y: (c.textRise / 100) + 'em',
        filter: 'blur(' + c.textBlur + 'px)'
      };
    },
    fade: function () { return { autoAlpha: 0 }; },
    'fade-up': function (d) { return { autoAlpha: 0, y: d }; },
    'fade-down': function (d) { return { autoAlpha: 0, y: -d }; },
    'fade-left': function (d) { return { autoAlpha: 0, x: d }; },
    'fade-right': function (d) { return { autoAlpha: 0, x: -d }; },
    'slide-up': function (d) { return { y: d }; },
    'slide-down': function (d) { return { y: -d }; },
    scale: function () { return { autoAlpha: 0, scale: 0.9 }; },
    'scale-down': function () { return { autoAlpha: 0, scale: 1.1 }; },
    blur: function (d) { return { autoAlpha: 0, y: d, filter: 'blur(12px)' }; },
    /* The original run's signature: an edge sweeping up rather than a
       crossfade, so no pixel is ever a blend of two chapters. clip-path does
       in one property what the old mask did in two, and animates on the
       compositor. */
    'wipe-up': function () { return { clipPath: 'inset(100% 0% 0% 0%)' }; },
    'wipe-down': function () { return { clipPath: 'inset(0% 0% 100% 0%)' }; },
    'wipe-left': function () { return { clipPath: 'inset(0% 0% 0% 100%)' }; },
    'wipe-right': function () { return { clipPath: 'inset(0% 100% 0% 0%)' }; }
  };

  /* Where a preset lands. Everything resolves to the same resting state, so
     this is one object rather than a mirror of ENTER — except clip-path,
     which has no resting value of its own and has to be told. */
  var REST = { autoAlpha: 1, x: 0, y: 0, scale: 1, filter: 'blur(0px)', clipPath: 'inset(0% 0% 0% 0%)' };

  function restFor(fromVars) {
    var to = {};
    for (var k in fromVars) {
      if (Object.prototype.hasOwnProperty.call(fromVars, k) && k in REST) to[k] = REST[k];
    }
    return to;
  }

  /* An exit is an enter run backwards, which is what makes "fade-up" mean the
     same shape whichever end of the section it is on: it arrives from below
     and it leaves upward. Distances are negated so it continues its travel
     rather than reversing into where it came from. */
  function exitVars(name, d) {
    var f = ENTER[name];
    if (!f) return null;
    var v = f(d);
    if (!v) return null;
    var out = {};
    for (var k in v) {
      if (!Object.prototype.hasOwnProperty.call(v, k)) continue;
      if (k === 'y' || k === 'x') out[k] = -v[k];
      else out[k] = v[k];
    }
    return out;
  }

  /* ---- reading a section ------------------------------------------------- */

  function attr(el, key, mb) {
    var v = mb ? el.getAttribute('data-fx-' + key + '-mb') : null;
    if (v === null || v === '') v = el.getAttribute('data-fx-' + key);
    return v === '' ? null : v;
  }

  function read(el, mb) {
    var speed = DEF.speed || 1;
    var scrub = attr(el, 'scrub', mb);
    return {
      on: attr(el, 'on', mb) || 'scroll',
      enter: attr(el, 'enter', mb) || DEF.enter,
      exit: attr(el, 'exit', mb) || DEF.exit,
      from: num(attr(el, 'from', mb), DEF.from),
      start: attr(el, 'start', mb) || DEF.start,
      exitStart: attr(el, 'exit-start', mb) || DEF.exitStart,
      duration: ms(attr(el, 'duration', mb), DEF.duration, speed),
      delay: ms(attr(el, 'delay', mb), DEF.delay, speed),
      ease: attr(el, 'ease', mb) || DEF.ease,
      child: attr(el, 'child', mb) || DEF.child,
      childFrom: num(attr(el, 'child-from', mb), DEF.childFrom),
      lead: ms(attr(el, 'lead', mb), DEF.lead, speed),

      /* Text has its own everything: its own preset, its own duration, and the
         two intervals that make up its rhythm. It is the one thing on the page
         animated per character rather than per element, and nothing else on
         the section is measured in those terms. */
      text: attr(el, 'text', mb) || DEF.text,
      textRise: num(attr(el, 'text-rise', mb), DEF.textRise),
      textBlur: num(attr(el, 'text-blur', mb), DEF.textBlur),
      textDuration: ms(attr(el, 'text-duration', mb), DEF.textDuration, speed),
      beat: ms(attr(el, 'beat', mb), DEF.beat, speed),
      gap: ms(attr(el, 'gap', mb), DEF.gap, speed),
      scrub: scrub === null || scrub === 'off' ? false : (scrub === 'true' ? true : num(scrub, 0)),
      pin: attr(el, 'pin', mb) === '1',
      pinLength: num(attr(el, 'pin-length', mb), 100),
      once: attr(el, 'once', mb) === '1'
    };
  }

  /* ---- splitting ---------------------------------------------------------
     Character and word units, so a heading can be written in rather than
     faded in. Done once and cached on the element: re-splitting already-split
     markup would wrap the wrappers.

     It walks into child elements rather than reading textContent, because the
     copy carries accent spans (.fch__mark) and flattening those would throw
     the tint away. Words are kept unbreakable so a unit is never split across
     two lines mid-animation. */
  function split(el, mode) {
    if (el.__fxSplit) return el.__fxSplit;

    var units = [];

    function walk(node) {
      var kids = Array.prototype.slice.call(node.childNodes);
      kids.forEach(function (child) {
        if (child.nodeType === 3) {
          var text = child.nodeValue;
          if (!text || !text.trim()) return;
          var frag = document.createDocumentFragment();
          var parts = mode === 'char' ? text.split('') : text.split(/(\s+)/);
          parts.forEach(function (part) {
            if (part === '') return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            var span = document.createElement('span');
            span.className = 'fx-u';
            span.textContent = part;
            frag.appendChild(span);
            units.push(span);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1) {
          walk(child);
        }
      });
    }

    walk(el);
    el.__fxSplit = units;
    return units;
  }

  /* The pieces that stagger. An element that asks to be split contributes its
     units instead of itself, so a heading is nine characters rather than one
     block, and everything is returned in document order — which is the order
     it reads in, and so the order it should be written in. */
  function pieces(root) {
    var marked = Array.prototype.slice.call(root.querySelectorAll('[data-fx-item], [data-fx-split]'));
    var els = [], groups = [], text = [];
    var g = 0;
    marked.forEach(function (el) {
      /* A nested scope owns its own children; leave them to it. */
      if (el.closest('[data-fx]') !== root) return;
      var mode = el.getAttribute('data-fx-split');
      if (mode === 'char' || mode === 'word') {
        split(el, mode).forEach(function (u) { els.push(u); groups.push(g); text.push(true); });
      } else {
        els.push(el); groups.push(g); text.push(false);
      }
      g++;
    });
    return { els: els, groups: groups, text: text };
  }

  /* ---- the rhythm --------------------------------------------------------
     A flat stagger is the wrong shape for split text. Twenty characters at one
     interval is twenty evenly spaced events, and the interval that reads well
     between two paragraphs is glacial between two letters: at 80ms a
     twenty-character heading takes 1.6 seconds to finish starting.

     So there are two intervals, which is what the original run had. A beat
     between one unit and the next inside the same element — 12ms, fast enough
     that a line reads as being written rather than as letters arriving — and a
     larger gap when the next unit belongs to a different element, which is
     what makes the hand-off from the heading to the body read as a line break
     instead of as more of the same line.

     Returned as absolute offsets rather than as a single interval, because
     that is what GSAP's function-form stagger wants: it asks per index and
     takes the answer as that target's delay. */
  function rhythm(groups, beat, gap) {
    var out = [];
    var t = 0;
    for (var i = 0; i < groups.length; i++) {
      if (i === 0) t = 0;
      else if (groups[i] !== groups[i - 1]) t += gap;
      else t += beat;
      out.push(t);
    }
    return out;
  }

  /* One fromTo per set, with the offsets that belong to it. Text and blocks
     are tweened apart because they do not share a duration: a letter settles
     in about half the time a whole stats row does, and one duration across
     both makes whichever it was not chosen for feel wrong. */
  function writeIn(tl, set, vars, dur, ease, offsets, at) {
    if (!set.length) return;
    tl.fromTo(
      set,
      vars,
      Object.assign({}, restFor(vars), {
        duration: dur,
        ease: ease,
        stagger: function (i) { return offsets[i]; }
      }),
      at
    );
  }

  /* ---- building ---------------------------------------------------------- */

  /* The stage is what actually moves for a section-level enter: the .fch
     stage where there is one, because that is the box with the background and
     the only thing worth wiping, and the scope itself otherwise. Moving the
     scope would move the scroll length with it. */
  function stageOf(root) {
    return root.querySelector(':scope > .fch__stage') || root;
  }

  /* The pieces of a section, split into the two sets that do not share a
     duration, each keeping the offsets its members were given — so the rhythm
     still runs through the section in document order even though it is played
     by two tweens. */
  function sets(root, c) {
    var p = pieces(root);
    var offsets = rhythm(p.groups, c.beat, c.gap);
    var out = { textEls: [], textOff: [], blockEls: [], blockOff: [] };
    p.els.forEach(function (el, i) {
      if (p.text[i]) { out.textEls.push(el); out.textOff.push(offsets[i]); }
      else { out.blockEls.push(el); out.blockOff.push(offsets[i]); }
    });
    var textFn = ENTER[c.text];
    var childFn = ENTER[c.child];
    out.textVars = textFn ? textFn(c.textRise, c) : null;
    out.childVars = childFn ? childFn(c.childFrom, c) : null;
    return out;
  }

  /* Just the contents arriving, on whatever trigger the caller describes. The
     free run hangs it off the section's own passage across the viewport; the
     held run hangs it off the chapter having essentially landed. Same
     timeline either way, which is what keeps a chapter's internal rhythm the
     same thing in both. */
  /* ---- resolving in -------------------------------------------------------
     03 does not show its device, it finds it: the picture starts small, out of
     focus and nearly unlit, and comes forward into focus and into the light
     while the last line of copy is still landing.

     In the original run that was a CSS transition waiting on .is-live, which
     the controller added the moment a chapter arrived. Here every chapter
     carries .is-live in its markup — the landed state IS the resting state
     this engine animates from — so a transition written that way has both its
     ends computed before the first paint and never runs at all. Which is why
     it has to be said from this side: the same gesture, one tween, on the
     chapter's own timeline so it keeps its place in the chapter's rhythm.

       data-fx-resolve        the element that resolves
       data-fx-resolve-scale  the size it starts at, as a fraction of the size
                              it rests at — so a picture that rests larger than
                              its own size still grows by the same proportion,
                              and the two breakpoints do not need two numbers.
                              Written as --fx-resolve-scale for the stylesheet
                              to multiply its resting size by
       data-fx-resolve-blur   how far out of focus it starts, px
       data-fx-resolve-dim    the brightness it starts at
       data-fx-resolve-x      how far to the side it starts, px. Written as
                              --fx-resolve-x for the stylesheet to place, for
                              the same reason as the size below
       data-fx-resolve-time   how long the whole of it takes, ms
       data-fx-resolve-lead   how far into its own box's arrival it begins, ms.
                              Into it, not after it: the fade and the resolve
                              overlap, so the hand-off between them is not an
                              edge
       data-fx-turn           a deck frame to hand over to as it lands, timed
                              off the deck's own crossfade so the second image
                              is completely there on the last frame of the
                              resolve. What that reads as is the device coming
                              forward and being already the other thing by the
                              time it can be read.

     The scale is relative and the filter is absolute, which is not an
     inconsistency: a picture rests at whatever size its chapter gives it, and
     there is no such thing as resting half-lit.

     Both of the geometric halves are handed over as custom properties rather
     than tweened directly, and that is not a preference. Touching x or scale
     puts CSSPlugin in charge of the whole transform, and the first thing it
     does is write `translate: none; rotate: none; scale: none` to take the
     standalone properties out of the way — which is where a chapter keeps the
     resting size this is supposed to be a fraction of. So the engine moves two
     numbers and the stylesheet keeps saying where the picture rests, which is
     the division of labour everywhere else in this file. */
  function resolvers(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-fx-resolve]'))
      .filter(function (el) { return el.closest('[data-fx]') === root; });
  }

  function resolveFrom(el) {
    return {
      '--fx-resolve-scale': num(el.getAttribute('data-fx-resolve-scale'), 0.7),
      '--fx-resolve-x': num(el.getAttribute('data-fx-resolve-x'), 0) + 'px',
      filter: 'blur(' + num(el.getAttribute('data-fx-resolve-blur'), 10) + 'px) ' +
              'brightness(' + num(el.getAttribute('data-fx-resolve-dim'), 0.3) + ')'
    };
  }

  /* CSS says its times in ms and GSAP wants seconds, and a custom property
     read back off the element has already had its var() resolved — so this is
     only ever handed a number with a unit on it. */
  function seconds(v, fallback) {
    var s = (v || '').trim();
    var n = parseFloat(s);
    if (isNaN(n)) return fallback;
    return s.indexOf('ms') > -1 ? n / 1000 : n;
  }

  function resolveIn(tl, root, c, k, base) {
    resolvers(root).forEach(function (el) {
      /* Placed against the arrival of the box it lives in rather than against
         the start of the chapter, so retiming the copy above it moves it too
         and the two stay one gesture. */
      var host = el.closest('[data-fx-item]');
      var i = host ? k.blockEls.indexOf(host) : -1;
      var at = base + (i > -1 ? k.blockOff[i] : 0) +
               ms(el.getAttribute('data-fx-resolve-lead'), 140, DEF.speed);
      var dur = ms(el.getAttribute('data-fx-resolve-time'), 1400, DEF.speed);

      tl.fromTo(
        el,
        resolveFrom(el),
        {
          '--fx-resolve-scale': 1,
          '--fx-resolve-x': '0px',
          filter: 'blur(0px) brightness(1)',
          duration: dur,
          ease: el.getAttribute('data-fx-resolve-ease') || c.ease
        },
        at
      );

      var turn = el.getAttribute('data-fx-turn');
      if (turn === null) return;
      /* data-active is read as an ancestor of the frames — that is what the
         shared mechanic's selectors ask for — so it is written where the deck
         carries it rather than where the resolve is marked. */
      var deck = el.closest('[data-active]');
      if (!deck) return;
      var back = deck.getAttribute('data-active') || '0';
      var fade = seconds(getComputedStyle(el).getPropertyValue('--fch-frame-fade'), 0.7);
      /* Started a crossfade short of the end rather than at it, and put back
         on the way out — a chapter scrolled past and returned to should be
         found the way it was first met. */
      tl.to(el, {
        duration: 0.001,
        onComplete: function () { deck.setAttribute('data-active', turn); },
        onReverseComplete: function () { deck.setAttribute('data-active', back); }
      }, Math.max(at, at + dur - fade));
    });
  }

  function contentTimeline(root, c, st) {
    var k = sets(root, c);
    if (!k.textVars && !k.childVars && !resolvers(root).length) return null;
    var tl = gsap.timeline({ scrollTrigger: st, defaults: { duration: c.duration, ease: c.ease } });
    var at = Math.max(0, c.lead);
    if (k.textVars) writeIn(tl, k.textEls, k.textVars, c.textDuration, c.ease, k.textOff, at);
    if (k.childVars) writeIn(tl, k.blockEls, k.childVars, c.duration, c.ease, k.blockOff, at);
    resolveIn(tl, root, c, k, at);
    return tl;
  }

  /* ---- waiting on the hero ------------------------------------------------
     data-fx-on="hero" is data-fx-on="load" with one condition on it: the film
     at the top of the page has to be over first. Nothing here knows anything
     about video — sections/hero-video-intro-gsap.liquid owns that ending and
     announces it, and this only listens.

     Three ways in, because a page can be in three states when a section is
     built: the ending has already happened (the editor reloading a section
     twenty seconds in), it has not happened yet, or there is no hero on the
     page at all — in which case there is nothing to wait for and holding the
     section back would hold it back forever. */
  function whenHeroPlayed(fn) {
    if (window.__fitxHeroPlayed) { fn(); return; }
    if (!document.querySelector('.hvi')) { fn(); return; }
    document.addEventListener('fitx:hero-played', fn, { once: true });
  }

  function onLoad(c) { return c.on === 'load' || c.on === 'hero'; }

  function build(root, mb, mm) {
    var c = read(root, mb);
    var stage = stageOf(root);
    var enterFn = ENTER[c.enter];
    var enterVars = enterFn ? enterFn(c.from, c) : null;
    var k = sets(root, c);

    /* A section fixed to the viewport has no passage across it to trigger on:
       the nav is position:fixed and is on screen from the first frame to the
       last, so "when its top is a fifth up the screen" is either always true
       or never. Those play once, on load, and say so.

       It is the only reason this exists. Everything that scrolls should be
       triggered by scrolling. */
    if (onLoad(c)) {
      var lt = gsap.timeline({ defaults: { duration: c.duration, ease: c.ease } });
      if (enterVars) {
        lt.fromTo(stage, enterVars, Object.assign({}, restFor(enterVars), { duration: c.duration, ease: c.ease }), c.delay);
      }
      var lAt = Math.max(0, c.delay + c.lead);
      if (k.textVars) writeIn(lt, k.textEls, k.textVars, c.textDuration, c.ease, k.textOff, lAt);
      if (k.childVars) writeIn(lt, k.blockEls, k.childVars, c.duration, c.ease, k.blockOff, lAt);
      resolveIn(lt, root, c, k, lAt);
      if (c.on === 'hero') {
        lt.pause(0);
        whenHeroPlayed(function () { lt.play(); });
      }
      return lt;
    }

    var st = {
      trigger: root,
      start: c.start,
      invalidateOnRefresh: true
    };

    if (c.pin) {
      st.pin = stage;
      st.pinSpacing = true;
      st.end = '+=' + c.pinLength + '%';
      st.anticipatePin = 1;
    }

    if (c.scrub !== false) {
      st.scrub = c.scrub;
      /* A scrubbed timeline needs a range to be scrubbed across. A pinned one
         already has its end; an unpinned one gets the section's own passage
         through the viewport, which is the reading of "as it goes by". */
      if (!st.end) st.end = 'bottom 40%';
    } else {
      st.toggleActions = c.once ? 'play none none none' : 'play none none reverse';
      if (c.once) st.once = true;
    }

    if (G.markers) st.markers = true;

    var tl = gsap.timeline({
      scrollTrigger: st,
      defaults: { duration: c.duration, ease: c.ease }
    });

    if (enterVars) {
      tl.fromTo(stage, enterVars, Object.assign({}, restFor(enterVars), { duration: c.duration, ease: c.ease }), c.delay);
    }

    /* Lead is measured from where the section's own enter begins, so the
       pieces can start inside it — which is what keeps a section from reading
       as two separate events, a box arriving and then its contents arriving
       after it. */
    var at = Math.max(0, c.delay + c.lead);
    if (k.textVars) writeIn(tl, k.textEls, k.textVars, c.textDuration, c.ease, k.textOff, at);
    if (k.childVars) writeIn(tl, k.blockEls, k.childVars, c.duration, c.ease, k.blockOff, at);
    resolveIn(tl, root, c, k, at);

    /* Everything a section shows over its own length rather than on arrival:
       image runs, drawn traces, cards rising. Given the section's range so
       they read against the same scroll the enter does. */
    progressive(root, c, st, mb);
    parallax(root, { trigger: root, start: 'top bottom', end: 'bottom top', invalidateOnRefresh: true });
    counters(root, { trigger: root, start: c.start, invalidateOnRefresh: true });
    measurePhone(root);

    /* The exit is its own trigger. It has to be: it starts somewhere the
       enter's trigger has long since passed, and one ScrollTrigger cannot
       hold two ranges. */
    if (c.exit && c.exit !== 'none') {
      var out = exitVars(c.exit, c.from);
      if (out) {
        gsap.timeline({
          scrollTrigger: {
            trigger: root,
            start: c.exitStart,
            toggleActions: 'play none none reverse',
            invalidateOnRefresh: true,
            markers: G.markers ? { startColor: 'red', endColor: 'red' } : false
          }
        }).to(stage, Object.assign({}, out, { duration: c.duration, ease: c.ease }));
      }
    }

    return tl;
  }

  /* ---- what a section shows over its own length --------------------------
     Three of the chapters are not one arrival but a thing developing as it is
     read: 04 builds a part across four photographs, 02 draws a trace onto a
     diagram, 02 and 10 raise a card up over the copy. In the original run all
     three were the controller's job, driven off the chapter's progress
     through its pin.

     They stay progress-driven here — that is what they are — but the progress
     is now this one section's own, so a chapter can be retimed without the
     ones either side of it moving. Each is written to the DOM as a number, a
     state or an index and resolved by the section's CSS, exactly as before:
     this file knows how far through the chapter the reader is and nothing
     about what that looks like.

       data-fx-frames="N"      run of N images; gets data-active, 0..N-1
       data-fx-frames-after-lift
                               count them from the end of the lift rather than
                               the start of the chapter
       data-fx-frame-loop="ms" run them on a timer instead of the scroll
       data-fx-frame-bounce    a loop turns round at the ends rather than
                               wrapping — a build that wraps undoes itself in
                               one jump, which is the one unnatural moment in
                               the cycle
       data-fx-trace           gets --fch-trace, 0 to 1
       data-fx-lift            gets --fch-lift, 0 to 1 over the first third,
                               and .is-landed once that finishes */
  /* querySelectorAll never returns the element it was called on, and three of
     these markers belong on the scope itself: 02 raises its card by writing
     --fch-lift and .is-landed on the chapter root, because that is where its
     stylesheet reads them (#fch-x.is-ready.is-landed, and the custom property
     by inheritance). Searching only the descendants would find nothing there
     and the card would never rise. */
  function within(root, selector) {
    var found = Array.prototype.slice.call(root.querySelectorAll(selector));
    if (root.matches(selector)) found.unshift(root);
    return found;
  }

  /* ---- path interpolation -------------------------------------------------
     The waveform's two paths are polylines of the same length at the same x
     positions, so walking between them is walking between two lists of y.
     This is what `transition: d` does in CSS; doing it here is what lets the
     scroll position choose the frame. */
  function points(d) {
    var n = d && d.match(/-?\d+(?:\.\d+)?/g);
    if (!n || n.length < 4 || n.length % 2) return null;
    var x = [], y = [];
    for (var i = 0; i < n.length; i += 2) { x.push(+n[i]); y.push(+n[i + 1]); }
    return { x: x, y: y };
  }

  function between(a, b, t) {
    var out = 'M';
    for (var i = 0; i < a.x.length; i++) {
      var v = a.y[i] + (b.y[i] - a.y[i]) * t;
      out += (i ? 'L' : '') + a.x[i] + ' ' + (Math.round(v * 100) / 100);
    }
    return out;
  }

  /* The same curve the hand-over uses, so a tune and a wipe running over each
     other are easing the same way. */
  function ease(t) { return smoother(t); }

  /* The share of a chapter's scroll the card's rise is given, everywhere the
     rise is not already owned by the window above (see `peeked`). Named
     because two mechanics have to agree on it: the lift spends it, and a run
     of frames marked data-fx-frames-after-lift waits it out. */
  var LIFT_SPAN = 0.33;

  function progressive(root, c, st, mb) {
    var frames = within(root, '[data-fx-frames]');
    var traces = within(root, '[data-fx-trace]');
    var lifts = within(root, '[data-fx-lift]');
    var waves = within(root, '[data-fx-wave]');
    if (!frames.length && !traces.length && !lifts.length && !waves.length) return;

    /* The range these read against. A pinned section already has one and it
       is the right one — the whole point of pinning is to buy scroll for
       exactly this. An unpinned section uses its own passage across the
       viewport. */
    var range = {
      trigger: root,
      start: st.start,
      end: st.end || 'bottom top',
      scrub: true,
      invalidateOnRefresh: true
    };

    /* A run on a timer does not touch the scroll at all, so it is set up once
       and only asked whether the section is on screen. Under a reduced-motion
       preference the timer does not run and the images fall back to the
       scroll — which is the useful fallback rather than simply stopping: a
       run stuck on its first image is three images nobody gets to see. */
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    Array.prototype.forEach.call(frames, function (deck) {
      var n = parseInt(deck.getAttribute('data-fx-frames'), 10) || 0;
      if (n < 2) return;
      /* Whether a run cycles itself or waits on the scroll is asked separately
         per width, because the two are genuinely different questions: four
         images worth cycling on a phone may be four images worth scrolling
         through on a desktop. Same fallback rule as everything else — a
         missing -mb answer is the desktop one. */
      var loop = parseInt(attr(deck, 'frame-loop', mb), 10) || 0;
      var bounce = deck.hasAttribute('data-fx-frame-bounce');
      var last = -1;

      function show(i) {
        if (i === last) return;
        last = i;
        deck.setAttribute('data-active', i);
      }

      if (loop && !reduced) {
        var i = 0;
        var dir = 1;
        var timer = null;
        ScrollTrigger.create({
          trigger: root,
          start: 'top bottom',
          end: 'bottom top',
          onToggle: function (self) {
            if (self.isActive && !timer) {
              timer = setInterval(function () {
                if (bounce) {
                  if (i + dir >= n || i + dir < 0) dir = -dir;
                  i += dir;
                } else {
                  i = (i + 1) % n;
                }
                show(i);
              }, loop);
            } else if (!self.isActive && timer) {
              clearInterval(timer);
              timer = null;
            }
          }
        });
        return;
      }

      /* A deck on a card that rises has to wait for it. The lift spends the
         first LIFT_SPAN of the chapter and the card's contents are drawn on
         landing, so frames counted from 0 give the first reading the rise and
         nothing else: it is over on the frame the card arrives, and the one
         reading the chapter opens on is the one nobody sees. Counted from the
         landing instead, all n readings are readings.

         Declared by the section rather than inferred from the presence of a
         lift, because a deck and a lift in the same chapter do not have to be
         the same picture. */
      var afterLift = deck.hasAttribute('data-fx-frames-after-lift');

      ScrollTrigger.create(
        Object.assign({}, range, {
          onUpdate: function (self) {
            var p = self.progress;
            if (afterLift) p = Math.max(0, (p - LIFT_SPAN) / (1 - LIFT_SPAN));
            /* Floor onto n buckets, with the last one kept reachable — a
               progress of exactly 1 would otherwise land on n and show
               nothing. */
            show(Math.min(n - 1, Math.floor(p * n)));
          }
        })
      );
    });

    Array.prototype.forEach.call(traces, function (el) {
      var lastV = -1;
      ScrollTrigger.create(
        Object.assign({}, range, {
          onUpdate: function (self) {
            var v = Math.round(self.progress * 1000) / 1000;
            if (v === lastV) return;
            lastV = v;
            el.style.setProperty('--fch-trace', v);
          }
        })
      );
    });

    /* ---- the waveform ----------------------------------------------------
       Two motions, and deliberately only ONE of them on the scroll.

       The tune. The signal is the untuned line — irregular, high amplitude —
       and the carrier is what it is being tuned onto. Handing the signal the
       carrier's `d` and letting a CSS transition walk between them is what the
       original did, and it is correct, but it happens on a 1200ms timer the
       moment the chapter arrives: the reader is somewhere else by the time it
       finishes, and it plays whether or not they were looking.

       Driven by the scroll instead, the reader IS the tuning. They scroll and
       the line converges; they stop and it holds where they left it; they
       scroll back and it comes apart again. Which is the claim the chapter is
       making — "it tunes itself" — demonstrated rather than illustrated.

       It completes at 60% of the window, and the window is not the chapter's
       box — roomPx() ends it where the NEXT chapter starts washing over this
       one. 85% was the first answer and it was too late by the only measure
       that counts: on this run that left about 150px of scroll between the
       tune landing and the wipe starting, less than the confirmation's own
       420ms fade, so the note the whole thing is building to was read through
       an incoming chapter or not at all. 60% leaves the better part of a
       screen with the line settled and the confirmation up, which is what
       "right before you pass it" was always supposed to buy.

       The drift stays on its own clock, which is the 19s CSS loop in
       fitx-chapter.css and is left running. Scrubbing it too was the tidier
       idea and the wrong one: with both motions answering the scroll, a reader
       who stops is looking at a still picture of a line, and a signal that
       holds perfectly still is not a signal. It has to keep waving while
       nothing is happening — from wherever it had got to, never restarted —
       and a CSS animation on a promoted layer does exactly that without asking
       the main thread for a frame. The svg holds two cycles and the pattern
       repeats at x=1436, so the loop's -50% lands the second exactly where the
       first began, and the wrap is invisible whenever it comes round.

       So the division is: the drift says the line is live, the tune answers
       the reader. Stopping mid-chapter leaves the wave moving at the
       convergence the reader scrolled it to, which is the state the chapter is
       claiming — it is tuning itself, and it has got this far.

       The dash creep is left alone for the same reason. It writes
       stroke-dashoffset, not transform, so it fights neither. */
    Array.prototype.forEach.call(waves, function (svg) {
      var carrier = svg.querySelector('.fch__wave-carrier');
      var signal = svg.querySelector('.fch__wave-signal');
      if (!carrier || !signal) return;

      /* The untuned shape, kept from the first time it is seen — after the
         first tune the attribute no longer holds it. */
      if (!signal.__fxLoose) signal.__fxLoose = signal.getAttribute('d');

      var loose = points(signal.__fxLoose);
      var tuned = points(carrier.getAttribute('d'));

      /* Both paths are the same 361 points at the same x positions, which is
         what makes them interpolable at all. If that ever stops being true —
         a redrawn carrier, a different signal — there is nothing sensible to
         walk between, so it falls back to the hand-over the original did and
         lets CSS transition it. */
      var pairs = loose && tuned && loose.x.length === tuned.x.length;

      var wave = svg.closest('.fch__wave') || svg;

      if (pairs) {
        /* --fch-wave-tune is the length of the CSS transition on `d` AND the
           delay on the confirmation underneath. Zeroing it does both jobs at
           once: the path stops lagging the scrub by 1200ms, and the note
           arrives with the tune instead of a beat after it. */
        wave.style.setProperty('--fch-wave-tune', '0ms');

        var step = -1;
        var written = false;
        ScrollTrigger.create(
          Object.assign({}, range, {
            onUpdate: function (self) {
              /* Complete early, then hold. */
              var t = Math.min(1, self.progress / 0.6);

              /* Quantised to a hundred stops. Rebuilding a 361-point `d`
                 string is about 4KB of allocation, and doing that on every
                 scroll event of a coasting trackpad is real work for a
                 difference no screen can resolve. A hundred shapes across a
                 chapter is smoother than the eye reads. */
              var q = Math.round(t * 100);

              /* The landed state is read off the quantised step, not off t,
                 and it is settled BEFORE the diff below can skip the rest of
                 this frame. Both halves of that were a bug: q rounds up to 100
                 while t is still a hair under 1 — a scroll that stops at 0.599
                 of the window is at t = 0.998, which is the hundredth step and
                 is not `t >= 1` — and the frame that first wrote the settled
                 path was therefore also the frame that returned early ever
                 after, so the confirmation the whole chapter builds to could
                 never be shown. It appeared only when a coarse jump happened
                 to land t exactly on 1, which is why it read as intermittent
                 rather than broken. Cached, so this touches the DOM on the one
                 frame the state actually changes. */
              var landed = q >= 100;
              if (landed !== written) {
                written = landed;
                root.classList.toggle('is-written', landed);
              }

              if (q === step) return;
              step = q;

              var k = ease(q / 100);
              signal.setAttribute('d', between(loose, tuned, k));
            }
          })
        );
      } else {
        ScrollTrigger.create({
          trigger: root,
          start: range.start,
          invalidateOnRefresh: true,
          onEnter: function () {
            signal.setAttribute('d', carrier.getAttribute('d'));
            root.classList.add('is-written');
          },
          onLeaveBack: function () {
            signal.setAttribute('d', signal.__fxLoose);
            root.classList.remove('is-written');
          }
        });
      }

      /* The sideways travel is the stylesheet's, and the only thing to do here
         is give it back if an earlier build in this session had taken it: a
         matchMedia context that switched the animation off and wrote an inline
         transform leaves both behind, and a running animation beats an inline
         transform, so the leftovers have to go in that order. */
      var drift = wave.querySelector('.fch__wave-drift');
      if (drift && drift.style.animation) {
        gsap.set(drift, { clearProps: 'transform' });
        drift.style.animation = '';
      }
    });

    /* A chapter whose picture was already begun by the chapter before it. The
       phone in chapter 10 is glimpsed at the foot of the one above — see
       plantPeek — and the two halves are one picture, so the lift that brings
       it forward cannot start where chapter 10's own scroll starts: by then
       the reader has been looking at the top of a phone for a screen and a
       half, and chapter 10 opens on it cropped, dark and empty and spends
       three quarters of a screen assembling itself in front of them. Which is
       what it was doing.

       The original run measured this from the peek: the lift begins where the
       chapter above finishes writing and ENDS just past the landing, so the
       picture is composed by the time the stage sticks — 0.59 of the way
       through at the landing itself, complete a fifth of a screen later. The
       window below is that one, stated against the viewport rather than
       measured off the copy above it, which is the one liberty taken. */
    var peekHost = root.querySelector('.fch__phone[data-part="bottom"]') &&
                   root.parentElement && root.parentElement.previousElementSibling;
    var peekClone = peekHost ? peekHost.querySelector('[data-fx-peek]') : null;
    var peeked = !!peekClone;

    var liftRange = peeked
      ? {
          trigger: root,
          start: function () { return 'top ' + Math.round(seenPx() * 0.3) + 'px'; },
          end: function () { return 'top top-=' + Math.round(seenPx() * 0.2); },
          scrub: true,
          invalidateOnRefresh: true
        }
      : range;

    Array.prototype.forEach.call(lifts, function (el) {
      var lastV = -1;
      var landed = false;

      /* The rest value, written now rather than waited for.
         ------------------------------------------------------------------
         --fch-lift is only ever set by the onUpdate below, and onUpdate does
         not run until the scroll first reaches this trigger's range. Until
         then the property is unset — and the stylesheet's fallback for it is
         1, not 0, deliberately: a phone in a chapter with no engine at all is
         meant to be a lit screen rather than a black rectangle.

         With an engine present that fallback is the wrong state, and it is
         the wrong state exactly once. On the way down the first time the
         screen is already fully lit before the phone has come forward, so the
         waking never happens; on every pass afterwards the property has been
         written, the screen rests black and lights as it should. That is a
         run that behaves differently on first read than on second, which is
         the one thing it must not do.

         So a chapter that HAS a lift states its rest value the moment it is
         built. The fallback is left alone and goes on meaning what it says:
         no engine, lit screen. */
      el.style.setProperty('--fch-lift', '0');
      ScrollTrigger.create(
        Object.assign({}, liftRange, {
          onUpdate: function (self) {
            /* Straight through on a peeked chapter, because the window above
               IS the rise. Everywhere else the rise is the first third of the
               section's scroll and the rest is reading time on a card that
               has already arrived. */
            var p = peeked ? self.progress : Math.min(1, self.progress / LIFT_SPAN);
            var v = Math.round(p * 1000) / 1000;
            if (v !== lastV) {
              lastV = v;
              el.style.setProperty('--fch-lift', v);
              /* The half of the phone the chapter before is drawing makes the
                 same move at the same moment. The lift is not only a rise —
                 the stylesheet multiplies --fch-phone-grow by it — so two
                 halves on different lifts are two halves at different SIZES,
                 and what the reader sees at the join is one phone drawn twice
                 with a second outline standing off the first. Which is what
                 was happening: the clone inherited the lift of the chapter it
                 stands in, and that chapter's lift had already reached 1 while
                 this one was six tenths of the way up.

                 Written to the clone rather than to that chapter's root, so a
                 chapter that raises a card of its own keeps its own lift. */
              if (peekClone) peekClone.style.setProperty('--fch-lift', v);
            }
            var isLanded = v >= 1;
            if (isLanded !== landed) {
              landed = isLanded;
              el.classList.toggle('is-landed', landed);
            }
          }
        })
      );
    });
  }



  /* ==========================================================================
     THE PHONE
     ==========================================================================
     Chapter 10 owns a phone standing on a field, and two things happen to it
     that are not animations of chapter 10.

     The peek. The top of the phone rises past the FOOT of chapter 06, a whole
     chapter before it is arrived at, so that the app has been glimpsed before
     it is introduced. That is one picture drawn by two chapters, and it is a
     clone rather than a second upload for a reason worth keeping: two
     chapters that have to agree on a file and a length are two chapters that
     will one day disagree, and did.

     The growth. As chapter 10 is read the phone comes forward and its screen
     grows to fill a stated share of the stage. How much bigger that is cannot
     be authored, because it depends on the stage height and on the natural
     size of the window cut in the artwork — so it is measured, once, and
     written as a number the stylesheet multiplies by --fch-lift.
     ========================================================================== */

  /* Cloned at init, and re-cloned on a rebuild: the editor can add or remove
     a section between the two chapters, and the peek belongs to whichever
     chapter is actually before this one at the time. */
  function plantPeek() {
    var old = document.querySelectorAll('[data-fx-peek]');
    Array.prototype.forEach.call(old, function (el) { el.parentNode.removeChild(el); });

    var owners = document.querySelectorAll('.fch__phone[data-part="bottom"]');
    Array.prototype.forEach.call(owners, function (phone) {
      var chapter = phone.closest('.fch');
      var host = chapter && chapter.parentElement;
      var before = host && host.previousElementSibling;
      if (!before || !before.classList.contains('fch-host')) return;

      var prev = before.querySelector('.fch');
      var stage = prev && prev.querySelector('.fch__stage');
      if (!stage || stage.querySelector('.fch__phone')) return;

      var clone = phone.cloneNode(true);
      clone.setAttribute('data-part', 'top');
      clone.setAttribute('data-fx-peek', '');
      clone.setAttribute('aria-hidden', 'true');
      /* The frames attribute comes off but data-active stays. The copy is
         pinned to the reading the run opens on — the picture the phone boots
         on — and the readings after it happen in the original, once that
         chapter has the screen to itself. A clone that advanced its own
         frames would be a second phone telling a different story in the
         corner of the chapter before. */
      clone.removeAttribute('data-fx-frames');
      clone.removeAttribute('data-fx-frame-loop');
      clone.removeAttribute('id');

      /* What the copy loses is the screen — the whole window, not a reading of
         it. The chapter before this one glimpses a DEVICE; the app is what
         chapter 10 is for, and showing it a chapter early spends the reveal
         before the chapter that exists to make it. The window is transparent
         and the stage behind it is dark, so what stands at the foot of
         chapter 06 is a phone with a black screen, which is what it should
         be.

         Cutting the window rather than the pictures inside it also settles
         two things at once: nothing in chapter 10's deck is fetched or
         decoded for a chapter the reader has not reached, and the peek can no
         longer light up by accident — see the lift pinned below.

         Nothing is lost at the join. The original's screen is dark at rest
         too: .fch__phone-screen carries brightness(--fch-lift * 4), so it
         only comes up as the phone comes forward, and by the time it does the
         peek is a chapter behind and off the screen. */
      var window_ = clone.querySelector('.fch__phone-screen');
      if (window_) window_.parentNode.removeChild(window_);

      /* And the copy is pinned at rest, because it is standing inside a
         chapter that has a lift of its own. --fch-lift inherits, chapter 06
         is marked data-fx-lift and drives its own to 1, and until chapter
         10's lift first fires there is nothing on the clone to override it —
         so on the way down the peek rises and grows on the WRONG chapter's
         scroll, which is one phone drawn twice with the halves at different
         sizes. Writing the rest value here means the clone never answers to
         anything but chapter 10. */
      clone.style.setProperty('--fch-lift', '0');

      /* How much of the phone stands above the foot of the chapter before.
         It has to reach that chapter's own ROOT and not the clone, because a
         chapter sized to its contents reserves the band the peek stands in
         out of its panel's padding — see .fch[data-fit] .fch__panel. Without
         it the band is not reserved, the clone begins exactly at the stage's
         bottom edge, and the glimpse the whole two-chapter picture is built
         on is a phone nobody can see: the sliver measured 0px where the
         original measured 80. */
      var show = getComputedStyle(chapter).getPropertyValue('--fch-phone-show');
      if (show) prev.style.setProperty('--fch-phone-show', show.trim());

      var rise = getComputedStyle(phone).getPropertyValue('--fch-phone-rise');
      if (rise) clone.style.setProperty('--fch-phone-rise', rise.trim());

      /* First child, as the original plants it: the peek stands behind that
         chapter's own copy rather than over it. */
      stage.insertBefore(clone, stage.firstChild);
    });
  }

  /* One measurement, written as a number. Never below 1: a screen short
     enough that the phone is already big enough is a phone that stays where
     it is, not one that comes forward by shrinking. Capped at 3 so a very
     short stage cannot ask for a phone the size of a building. */
  function measurePhone(root) {
    var phone = root.querySelector('.fch__phone[data-part="bottom"]');
    var stage = stageOf(root);
    if (!phone || stage === root) return;

    var window_ = phone.querySelector('.fch__phone-screen');
    var fill = parseFloat(getComputedStyle(phone).getPropertyValue('--fch-screen-fill')) || 0;
    var natural = window_ ? window_.offsetHeight : 0;
    if (!(fill > 0 && natural > 0)) return;

    /* Against what the reader can see, not against the stage's box. The stage
       carries a bleed below the fold so the chapter keeps covering when the
       phone's bars roll away, and sizing the phone to include that band would
       grow it by the height of the browser chrome — a phone that is bigger on
       a phone than it is anywhere else. */
    var seen = lengthPx(root, '--fch-seen', stage.offsetHeight);
    var scale = (fill / 100) * Math.min(stage.offsetHeight, seen) / natural;
    if (scale < 1) scale = 1;
    if (scale > 3) scale = 3;
    var grow = Math.round((scale - 1) * 1000) / 1000;

    root.style.setProperty('--fch-phone-grow', grow);
    /* The copy standing in the chapter before has to make the same move, or
       the two halves of one picture come forward by different amounts. */
    var peek = document.querySelector('[data-fx-peek]');
    if (peek) {
      var peekChapter = peek.closest('.fch');
      if (peekChapter) peekChapter.style.setProperty('--fch-phone-grow', grow);
    }
  }

  /* ==========================================================================
     DEPTH
     ==========================================================================
     Two drivers that exist only to make the run read as cinematic rather than
     as a sequence of arrivals: things at different distances moving at
     different speeds, and numbers that count rather than appear.
     ========================================================================== */

  /* Parallax. An element marked data-fx-parallax="N" travels N px against the
     scroll across the section's own range, so media set behind copy drifts
     while the copy holds. Scrubbed, always — parallax that plays on a trigger
     is not parallax, it is a slide. */
  function parallax(root, range) {
    var els = Array.prototype.slice.call(root.querySelectorAll('[data-fx-parallax]'));

    /* A chapter asks for its media to drift by stating one number on itself,
       rather than by the media being marked — because which element IS the
       media differs per chapter (a deck, a pair of frames, a glow behind a
       device) and .fch__media is the one thing they all share. Asked on the
       root and resolved here, a chapter gets parallax by turning a slider. */
    var d0 = parseFloat(root.getAttribute('data-fx-media-parallax')) || 0;
    if (d0) {
      Array.prototype.forEach.call(root.querySelectorAll('.fch__media'), function (m) {
        if (!m.hasAttribute('data-fx-parallax')) {
          m.setAttribute('data-fx-parallax', d0);
          els.push(m);
        }
      });
    }

    els.forEach(function (el) {
      var d = parseFloat(el.getAttribute('data-fx-parallax')) || 0;
      if (!d) return;
      gsap.fromTo(el, { y: d }, {
        y: -d,
        ease: 'none',
        scrollTrigger: Object.assign({}, range, { scrub: true })
      });
    });
  }

  /* Counters. A stat marked data-fx-count rolls from 0 to whatever it already
     reads, so a figure arrives as a measurement being taken rather than as a
     number fading in.

     The text is parsed rather than authored twice: whatever is in the element
     is the target, and any prefix or suffix around the digits is kept and put
     back each frame. So "4 min" counts to 4 and keeps its unit, and "Live"
     has no digits and is left entirely alone. */
  function counters(root, range) {
    var els = within(root, '[data-fx-count]');
    els.forEach(function (el) {
      var raw = el.textContent.trim();
      var m = raw.match(/^(\D*?)(\d[\d.,]*)(.*)$/);
      if (!m) return;

      var before = m[1], after = m[3];
      var digits = m[2].replace(/,/g, '');
      var target = parseFloat(digits);
      if (isNaN(target)) return;
      var dp = (digits.split('.')[1] || '').length;
      var group = m[2].indexOf(',') > -1;

      var box = { v: 0 };
      gsap.to(box, {
        v: target,
        ease: 'power2.out',
        duration: 1.4,
        scrollTrigger: Object.assign({}, range, { toggleActions: 'play none none reset' }),
        onUpdate: function () {
          var n = box.v.toFixed(dp);
          if (group) n = Number(n).toLocaleString();
          el.textContent = before + n + after;
        }
      });
    });
  }

  /* ==========================================================================
     THE HELD RUN
     ==========================================================================
     A run of adjacent chapters that behaves as one pinned sequence: the first
     reaches the viewport and sticks, and from then on something is always
     filling the screen while each chapter hands over to the next and the
     ground washes between dark and light.

     None of that geometry is rebuilt here. fitx-chapter.css already pulls each
     chapter up on top of the one before it, sticks the stages at top:0, and
     carries the three hand-over styles; the -gsap chapters declare `fch-host`
     so those rules match them. What this module does is drive it — which used
     to be a rAF loop measuring and diffing every chapter every frame, and is
     now one scrubbed ScrollTrigger per hand-over.

     The arithmetic is the original's, deliberately unchanged. A chapter's box
     top travelling from `fade` to 0 IS its progress through the hand-over:

         p = 1 - offset / fade      offset = box top - scroll position

     which is exactly the range a ScrollTrigger describes as starting when the
     chapter's top is `fade` down the viewport and ending when it reaches the
     top. So p is the trigger's own progress, and there is nothing to measure.

     What each style does with it, also the original's:

       wipe    --fch-reveal, -18% to 100%. A masked edge sweeping up. -18% is
               the mask fully open at the bottom edge and 100% is solid. The
               default, and the only one that stays clean across a dark/light
               change: fading an opaque light stage over an opaque dark one
               drags every pixel through mid-grey, and no easing fixes that
               because grey is genuinely the midpoint.
       slide   --fch-slide, 100% to 0. The stage translating up and covering.
       fade    --fch-opacity, 0 to 1. Fine between chapters sharing a scheme.
       scroll  nothing. Opts out of the overlap entirely — the chapter holds
               for its dwell, scrolls away, and the next arrives from below.

     Smootherstep on the way, so the edge eases off the screen rather than
     stopping dead. ScrollTrigger's scrub is linear by design and that is
     correct — the easing belongs to the value, not to the scrubbing.
     ========================================================================== */

  /* Which hand-over a chapter's Arrives-by setting means. The free run has
     fifteen presets because a section standing alone can arrive any way it
     likes; a chapter arriving OVER another one has four, and they are about
     how the overlap resolves rather than about where the box comes from. So
     the vocabulary is folded rather than duplicated: every wipe is a wipe,
     every slide a slide, and anything that is not one of those and is not
     "nothing" is a fade. */
  function handover(enter) {
    if (!enter || enter === 'none') return 'scroll';
    if (enter.indexOf('wipe') === 0) return 'wipe';
    if (enter.indexOf('slide') === 0) return 'slide';
    return 'fade';
  }

  /* Whether a chapter is a screen that can be washed over. A `data-fit` or
     `data-short` chapter is as tall as its own contents and does not stick:
     there is no held screen there, and the overlap that would pull the next
     chapter over it reaches past its top into whatever is above — on this
     template, the hero. */
  function holdsStage(chapter) {
    return !chapter.hasAttribute('data-fit') && !chapter.hasAttribute('data-short');
  }

  /* Does this chapter arrive OVER the one before it? Both halves matter: what
     the chapter asks for, and whether there is anything under it to arrive
     over. fitx-gsap.css takes the overlap off by exactly this test — the two
     have to agree, or the engine masks a chapter that layout never made room
     for, or leaves one lifted with no wash to explain where it went. */
  function washes(chapter, prev, mb) {
    if (!chapter || !prev) return false;
    return holdsStage(prev) && handover(read(chapter, mb).enter) !== 'scroll';
  }

  function smoother(t) {
    /* Flat at both ends. */
    return t * t * t * (t * (t * 6 - 15) + 10);
  }

  /* What a length written on an element actually comes to, in px.
     ------------------------------------------------------------------------
     Measured with a probe rather than parsed. These values used to be plain
     vh and a parseFloat was enough; they are now calc() over --fch-vh — see
     the block at the top of fitx-chapter.css — and parseFloat('calc(...)') is
     NaN. Handing the browser the declaration and asking how tall it came out
     is the only reading that stays right whatever the stylesheet writes next,
     and it is the reading the stylesheet itself uses.

     Fixed rather than absolute: an absolutely positioned probe two screens
     tall would extend the document, and the run measures the document. */
  var lenProbe = null;

  /* One screen, in px, as the run has AGREED it — not as the window happens to
     report it this instant.
     ------------------------------------------------------------------------
     window.innerHeight is not a constant on a phone. The address bar rolls
     away under the reader's own first scroll and the number grows, so a range
     computed from it while the reader is moving is a range that moves too:
     the trigger's start and end shift out from under the scroll that is
     driving them, once, on the way down. Coming back up the bar is already
     gone and the number is steady, which is why the same picture is correct
     on the way back and wrong on the way there.

     --fch-vh is locked for exactly this reason — see the controller — so a
     range measured off it holds still for the whole run. Falls back to the
     live value if the lock has not been written, which is a page with no
     controller on it. */
  function seenPx() {
    var v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--fch-vh'));
    return v > 0 ? v * 100 : window.innerHeight;
  }

  function lengthPx(el, prop, fallback) {
    var v = getComputedStyle(el).getPropertyValue(prop).trim();
    if (!v) return fallback;
    if (!lenProbe) {
      lenProbe = document.createElement('div');
      lenProbe.style.cssText = 'position:fixed;top:0;left:0;width:0;visibility:hidden;pointer-events:none';
      document.body.appendChild(lenProbe);
    }
    lenProbe.style.height = v;
    return lenProbe.offsetHeight || fallback;
  }

  /* The overlap, in px. Read from the stylesheet rather than assumed, so the
     one place it is stated stays the one place it is stated. */
  function fadePx(el) {
    return lengthPx(el, '--fch-fade', seenPx() * 0.45);
  }

  /* How much scroll a chapter actually owns the screen for.
     ------------------------------------------------------------------------
     Not its box. A chapter's box is 100vh + dwell, but the NEXT chapter is
     pulled up by 100vh + fade, so it begins washing over this one long before
     this one's box has finished:

         next.top  =  this.top + dwell - fade
         next starts its wipe at  next.top - fade  =  this.top + dwell - 2*fade

     So the window in which this chapter is the thing on screen — the window
     everything it shows over its own length has to fit inside — is
     `dwell - 2*fade`, and with the run's 45vh fade a dwell under 90vh has no
     solo window at all.

     Getting this wrong is not subtle. Running a tune, a build or a rising
     card across the whole box instead means they finish two chapters later,
     off-screen, having appeared to stall at whatever fraction they reached
     before the wash took them.

     Measured off the tops rather than computed from dwell and fade directly,
     so it stays right whatever transition the next chapter uses — one that
     opts out of the overlap does not steal any of this chapter's room. */
  function roomPx(root, next, mb) {
    var vh = seenPx();
    var room = lengthPx(root, '--fch-dwell', vh);

    if (washes(next, root, mb)) {
      var gap = next.getBoundingClientRect().top - root.getBoundingClientRect().top;
      var begins = gap - fadePx(next);
      if (begins < room) room = begins;
    }
    return Math.max(1, Math.round(room));
  }

  function buildHeld(root, mb, index, run) {
    var c = read(root, mb);
    var stage = stageOf(root);
    var style = handover(c.enter);
    var prev = run[index - 1] || null;

    /* A chapter with nothing to arrive over is simply always landed: first in
       its run, one that opted out of the overlap, or one whose predecessor is
       a fit chapter rather than a held screen. It carries no mask, and a
       masked full-viewport layer is a whole-screen repaint, so not asking for
       one is worth the branch. */
    var lands = washes(root, prev, mb);

    /* The hand-over the stylesheet is about to be asked to perform, said in
       the vocabulary it reads. Everything that makes a wipe a wipe — the
       masked edge, the slide's transform, the fade's opacity — is mounted on
       `.fch[data-transition='…'] .fch__stage.is-wiping`, and the -gsap
       sections have no data-transition to match on: the engine was setting
       --fch-reveal on a stage with no mask to reveal, so an incoming chapter
       simply covered the one below it with a hard edge, whatever it had been
       asked to do. Written from here rather than from Liquid because this is
       where the inheritance has already been resolved, and because it costs
       nothing to be late: the mask only mounts alongside .is-wiping, which is
       this engine's to add too. Removed when the chapter does not land, so a
       breakpoint change cannot leave a chapter claiming a hand-over it no
       longer performs. */
    if (lands) root.setAttribute('data-transition', style);
    else root.removeAttribute('data-transition');

    if (lands) {
      var last = null;

      ScrollTrigger.create({
        trigger: root,
        start: function () { return 'top ' + fadePx(root) + 'px'; },
        end: 'top top',
        scrub: true,
        invalidateOnRefresh: true,
        markers: G.markers ? { startColor: 'cyan', endColor: 'cyan' } : false,

        onUpdate: function (self) {
          var p = self.progress;
          var eased = smoother(p);
          var prop, value;

          if (style === 'slide') {
            prop = '--fch-slide';
            value = Math.round((1 - eased) * 1000) / 10 + '%';
          } else if (style === 'fade') {
            prop = '--fch-opacity';
            value = Math.round(eased * 100) / 100 + '';
          } else {
            prop = '--fch-reveal';
            value = Math.round(-18 + 118 * eased) + '%';
          }

          /* Diffed, because this runs on every scroll event and an unchanged
             frame should touch no DOM at all. The values are already rounded
             finer than the screen can resolve, so the diff fires only on
             changes somebody could actually see. */
          if (value !== last) {
            last = value;
            stage.style.setProperty(prop, value);
          }
        },

        /* .is-wiping mounts the mask, and only for the window it is needed:
           at rest — which is where a reader spends nearly all of their time —
           no chapter carries one. */
        onToggle: function (self) {
          stage.classList.toggle('is-wiping', self.isActive);
        },

        /* Past halfway the arriving chapter is most of what is on screen, so
           it should be what answers a click. Before that the reader can see
           the chapter underneath and should be able to reach it. */
        onEnter: function () { stage.classList.remove('is-off'); },
        onEnterBack: function () { stage.classList.remove('is-off'); },
        onLeaveBack: function () { stage.classList.add('is-off'); },

        onRefresh: function (self) {
          stage.classList.toggle('is-off', self.progress <= 0);
          stage.classList.toggle('is-wiping', self.progress > 0 && self.progress < 1);
        }
      });

      /* Halfway is where the pointer changes hands, and it is its own trigger
         because it is a state rather than a value: a chapter resting slightly
         short of its landing point is still mid-wash by the arithmetic, and
         without this its controls would be dead while looking perfectly
         alive. An accordion you can read and cannot open is worse than one
         that is not there. */
      ScrollTrigger.create({
        trigger: root,
        start: function () { return 'top ' + (fadePx(root) * 0.5) + 'px'; },
        end: 'top top',
        onToggle: function (self) { stage.classList.toggle('is-clear', self.isActive); },
        onRefresh: function (self) { stage.classList.toggle('is-clear', self.progress > 0); }
      });

      /* The chapter behind stops painting once this one covers it. One stage
         on screen instead of ten, which on a run of full-viewport layers is
         the difference between a scroll that coasts and one that does not. */
      if (prev) {
        ScrollTrigger.create({
          trigger: root,
          start: 'top top',
          end: 'bottom top',
          onToggle: function (self) {
            var pstage = stageOf(prev);
            if (pstage !== prev) pstage.classList.toggle('is-off', self.isActive);
          }
        });
      }
    } else {
      stage.classList.remove('is-off');
    }

    /* The copy waits for the ground — for the ground under ITSELF, which is
       not the same as waiting for the whole hand-over to finish. The mask
       sweeps up from the bottom edge, so the band the copy occupies is opaque
       by a little past halfway; the original's 0.72 was measured on a run
       whose chapters were not full-height and here it left the incoming
       screen standing empty for the last third of the wash — a light, blank
       chapter arriving before it had anything to say, then writing itself
       after the reader had already been handed it.

       Starting where the edge clears the copy instead, the words arrive
       through the rising edge and are settled about when the ground is. Not
       earlier than that: a word written above the edge is written onto the
       chapter underneath, which is the racing-the-wash the original was right
       to avoid. The copy is masked with everything else, so nothing is
       readable before its own ground is there — the mask is what makes the
       earlier start safe. */
    var contentStart = lands
      ? function () { return 'top ' + (fadePx(root) * 0.45) + 'px'; }
      : 'top 78%';

    contentTimeline(root, c, {
      trigger: root,
      start: contentStart,
      toggleActions: c.once ? 'play none none none' : 'play none none reverse',
      invalidateOnRefresh: true,
      markers: G.markers ? { startColor: 'lime', endColor: 'lime' } : false
    });

    /* Everything the chapter shows over its own length rather than on
       arrival — the image runs, the trace, the rising card, the phone growing
       forward. Measured from where it lands to where it starts being covered,
       which is the dwell: the scroll it bought with its own box height. */
    var next = run[index + 1] || null;
    var dwell = {
      trigger: root,
      start: 'top top',
      end: function () { return '+=' + roomPx(root, next, mb); },
      invalidateOnRefresh: true
    };
    progressive(root, c, dwell, mb);
    parallax(root, dwell);
    counters(root, { trigger: root, start: contentStart, invalidateOnRefresh: true });
    measurePhone(root);
  }

  /* Consecutive chapters are one run; anything else between them starts a new
     one. The same adjacency the stylesheet uses to apply the overlap, so the
     two cannot disagree about where a run begins. */
  function runs() {
    var hosts = Array.prototype.slice.call(document.querySelectorAll('.fx-host.fch-host'));
    var out = [], current = [];
    hosts.forEach(function (host, i) {
      var chapter = host.querySelector('.fch--gsap');
      /* data-fx-static: a chapter whose section turned its scroll animation
         off (App screens) runs itself and is not part of a held run. */
      if (!chapter || chapter.hasAttribute('data-fx-static')) return;
      var prev = hosts[i - 1];
      if (current.length && prev && prev.nextElementSibling === host) current.push(chapter);
      else { if (current.length) out.push(current); current = [chapter]; }
    });
    if (current.length) out.push(current);
    return out;
  }

  /* Set the from-state immediately, before ScrollTrigger has decided anything,
     so a section below the fold is not briefly painted at rest and then
     snatched back to hidden. Only for sections that are actually below the
     fold — one already on screen should stay exactly as it is, because it is
     what the reader is looking at. */
  function prepare(root, mb) {
    var c = read(root, mb);
    if (!onLoad(c)) {
      var box = root.getBoundingClientRect();
      if (box.top < seenPx() * 0.9) return;
    }
    var enterFn = ENTER[c.enter];
    if (enterFn && enterFn(c.from, c)) gsap.set(stageOf(root), enterFn(c.from, c));
    prepareContent(root, c);
  }

  /* Just the contents. The held run needs this half on its own: a chapter
     arrives by having a mask swept off it, so its stage must not also be
     sitting at an enter preset's from-state — but its copy still has to be
     held back, or it is briefly readable through the wipe it is supposed to
     be written after. */
  function prepareContent(root, c) {
    var p = pieces(root);
    var textFn = ENTER[c.text];
    var childFn = ENTER[c.child];
    p.els.forEach(function (el, i) {
      var fn = p.text[i] ? textFn : childFn;
      var v = fn && fn(p.text[i] ? c.textRise : c.childFrom, c);
      if (v) gsap.set(el, v);
    });
    /* A picture that resolves is held at its start too, or the reader meets it
       finished and it snaps back to the beginning to be found. */
    resolvers(root).forEach(function (el) { gsap.set(el, resolveFrom(el)); });
  }

  /* A held chapter that has not arrived yet is hidden before the first paint,
     not after ScrollTrigger's first refresh. Every chapter after the first is
     laid out ON TOP of the one before it and painted later, so a stack left
     visible for even one frame shows the LAST chapter in the run over
     everything else. That is the one flash worth spending a layout read on. */
  function prepareHeld(root, mb, index, run) {
    var c = read(root, mb);
    prepareContent(root, c);
    if (!washes(root, run[index - 1] || null, mb)) return;
    var stage = stageOf(root);
    if (root.getBoundingClientRect().top > fadePx(root)) stage.classList.add('is-off');
  }

  /* ---- the run ----------------------------------------------------------- */

  var mm = null;

  function scopes() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-fx]'));
  }

  function start() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    gsap.registerPlugin(ScrollTrigger);

    /* The same address bar, told to ScrollTrigger directly. Its own refresh is
       bound to resize, and on a phone the first scroll IS a resize — the bar
       rolls away and every trigger on the page recomputes in the middle of the
       gesture that is driving them. That is a run that composes itself wrongly
       once, on the way down, and is correct ever after, which is the shape of
       the fault this fixes. GSAP ships the switch for it because the problem
       is universal; the run just never asked for it. */
    ScrollTrigger.config({ ignoreMobileResize: true });
    /* Named so a section can ask for it by name from its Ease dropdown, the
       same way it asks for power3.out. */
    gsap.registerEase('apple', bezier(0.16, 1, 0.3, 1));
    gsap.config({ nullTargetWarn: false });

    if (mm) mm.revert();
    mm = gsap.matchMedia();

    /* Before any measuring: the clone changes what chapter 06's stage contains,
       and a trigger built against the stage without it is built against the
       wrong height. */
    plantPeek();

    /* Held is the default because it is what the run is: a sequence of
       chapters that hand over to one another. Free is the escape hatch for a
       page that wants them to be ordinary sections. */
    var mode = G.run || 'held';

    function assemble(mb) {
      if (mode !== 'held') {
        scopes().forEach(function (root) { prepare(root, mb); build(root, mb); });
        return;
      }

      /* Chapters are driven as runs; everything else on the page — the nav,
         the hero, the footer — is an ordinary section and is built the same
         way it is in the free run. */
      var held = [];
      runs().forEach(function (run) {
        run.forEach(function (chapter, i) {
          held.push(chapter);
          prepareHeld(chapter, mb, i, run);
          buildHeld(chapter, mb, i, run);
        });
      });

      scopes().forEach(function (root) {
        if (held.indexOf(root) > -1) return;
        prepare(root, mb);
        build(root, mb);
      });
    }

    mm.add(DESKTOP + ' and (prefers-reduced-motion: no-preference)', function () { assemble(false); });
    mm.add(MOBILE + ' and (prefers-reduced-motion: no-preference)', function () { assemble(true); });

    /* Reduced motion: everything at rest, no triggers. Split units are left
       split — they are inline-block spans of the same text, so the paragraph
       reads identically and nothing has to be put back. */
    mm.add('(prefers-reduced-motion: reduce)', function () {
      scopes().forEach(function (root) {
        var stage = stageOf(root);
        gsap.set(stage, REST);
        /* The run's own transients, which no tween owns and so nothing else
           would clear: a chapter left masked is a chapter left invisible. */
        stage.classList.remove('is-wiping', 'is-off');
        stage.classList.add('is-clear');
        var p = pieces(root);
        if (p.els.length) gsap.set(p.els, REST);
        var r = resolvers(root);
        if (r.length) gsap.set(r, { '--fx-resolve-scale': 1, '--fx-resolve-x': '0px', filter: 'blur(0px) brightness(1)' });
      });
    });

    /* Late-arriving images change every position below them, and a trigger
       measured against the old ones fires early for the rest of the session. */
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  }

  window.__fitxGsap = {
    refresh: function () { start(); },
    build: build,
    ENTER: ENTER
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* The editor reloads a section into the page without reloading the page, so
     the new markup has no triggers on it and the old section's triggers are
     pointing at elements that are gone. */
  document.addEventListener('shopify:section:load', function () { start(); });
  document.addEventListener('shopify:section:unload', function () { start(); });
})();
